// Toolbar.jsx — оптимізована верхня панель
import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import FeatureSettingsMenu from './FeatureSettingsMenu';
import imgBadge from './assets/drive-logo.svg';

/* ——— UI утиліти ——— */
const btnBase =
    'inline-flex items-center gap-2 rounded-lg border px-4 py-1.5 text-sm font-medium shadow-sm transition active:scale-[0.98]';
const btnDefault =
    `${btnBase} border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800`;

/* ——— Іконки ——— */
const IconGoogle = memo(function IconGoogle({ className = 'h-5 w-5' }) {
    return (
        <svg aria-hidden viewBox="0 0 48 48" className={className}>
            <path fill="#EA4335" d="M24 9.5c3.9 0 7.1 1.3 9.7 3.7l7.2-7.2C36.3 2.4 30.6 0 24 0 14.7 0 6.7 5.3 2.7 13l8.4 6.5C12.6 13.6 17.8 9.5 24 9.5z" />
            <path fill="#34A853" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9.1h12.4c-.5 2.8-2 5.2-4.3 6.9l6.7 5.2C42.9 37.4 46.1 31.4 46.1 24.5z" />
            <path fill="#4A90E2" d="M9.1 28.4c-.5-1.4-.8-2.9-.8-4.4s.3-3 .8-4.4L.7 13.1C-1 16.7-1 20.3-.7 24c.3 3.7 1.3 7.2 3.1 10.3l8.4-5.9C10.3 27.7 9.7 28 9.1 28.4z" />
            <path fill="#FBBC05" d="M24 48c6.5 0 12.1-2.1 16.1-5.8l-7.7-6c-2.1 1.5-4.8 2.4-8.4 2.4-6.1 0-11.3-4.1-13.2-9.6L2.7 34.9C6.7 42.7 14.7 48 24 48z" />
        </svg>
    );
});

const IconLogout = memo(function IconLogout({ className = 'h-5 w-5' }) {
    return (
        <svg aria-hidden viewBox="0 0 24 24" className={className} fill="none">
            <path d="M13 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M10 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M15 9l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
});

const DriveBadge = memo(function DriveBadge() {
    return (
        <span
            className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-2xl shadow-sm dark:border-neutral-700 dark:bg-neutral-900">
            <img
                src={imgBadge}
                alt=""
                aria-hidden="true"
                className="h-[1.5em] w-[1.5em] inline-block align-middle"
                loading="lazy"
                decoding="async"
            />
            <span className="text-2xl font-semibold">Google Drive</span>
        </span>
    );
});

const GoogleLoginButton = memo(function GoogleLoginButton({ onClick }) {
    return (
        <button
            type="button"
            className={btnDefault}
            onClick={onClick}
            title="Увійти через Google"
            aria-label="Увійти через Google"
        >
            <IconGoogle />
            <span>Увійти через Google</span>
        </button>
    );
});
GoogleLoginButton.propTypes = { onClick: PropTypes.func.isRequired };

function LogoutButton({ onClick }) {
    return (
        <button
            type="button"
            className={btnDefault}
            onClick={onClick}
            title="Стерти сесію і вийти"
            aria-label="Вийти"
        >
            <IconLogout />
            <span>Вийти</span>
        </button>
    );
}
LogoutButton.propTypes = { onClick: PropTypes.func.isRequired };

function Toolbar({ authed, onLogout, fmgr, loadForest }) {
    const handleLogin = useCallback(() => {
        window.location.assign('/drive.php?action=auth-start');
    }, []);

    return (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
                <DriveBadge />
                {!authed && <GoogleLoginButton onClick={handleLogin} />}
            </div>

            {authed && (
                <div className="flex items-center gap-3">
                    <FeatureSettingsMenu fmgr={fmgr} loadForest={loadForest} />
                    <LogoutButton onClick={onLogout} />
                </div>
            )}
        </div>
    );
}

Toolbar.propTypes = {
    authed: PropTypes.bool.isRequired,
    onLogout: PropTypes.func.isRequired,
    fmgr: PropTypes.object.isRequired,
    loadForest: PropTypes.func,
};

export default memo(Toolbar);

IconLogout.propTypes = {
    className: PropTypes.string,
};
IconLogout.defaultProps = {
    className: 'h-5 w-5',
};
IconGoogle.propTypes = {
    className: PropTypes.string,
};
IconGoogle.defaultProps = {
    className: 'h-5 w-5',
};
